import { useEffect, useState } from 'react';
import { App, Button, Form, Input, InputNumber, Select, Space, Typography } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import {
  NOC_SIM_COMMANDS,
  NOC_SIM_DESTRUCTIVE_COMMANDS,
  type NocSimCommand,
  type NocSimCommandResult,
} from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { simApi } from '../api/simApi';
import { RESET_TYPES, TRIGGER_MESSAGES, buildChargingProfile } from '../constants';
import { CommandResultView } from './SimResult';

const COMMAND_LABELS: Record<NocSimCommand, string> = {
  remoteStart: 'RemoteStart — iniciar carga',
  remoteStop: 'RemoteStop — detener carga',
  reset: 'Reset — reiniciar el cargador',
  unlockConnector: 'UnlockConnector — desbloquear conector',
  clearCache: 'ClearCache — limpiar caché de autorizaciones',
  triggerMessage: 'TriggerMessage — pedir mensaje al cargador',
  setChargingProfile: 'SetChargingProfile — perfil de carga (DLM)',
};

const CONNECTOR_COMMANDS: readonly NocSimCommand[] = [
  'remoteStart',
  'remoteStop',
  'unlockConnector',
  'triggerMessage',
  'setChargingProfile',
];

interface CommandFormValues {
  command: NocSimCommand;
  connectorId?: number;
  idTag?: string;
  resetType?: string;
  requestedMessage?: string;
  limitA?: number;
}

interface CommandPanelProps {
  stationId: string | null;
  connectorId: number | null;
  protocol: string | null;
}

/** Panel de comandos al EVSE (downlink, proxy Message API del core). */
export function CommandPanel({ stationId, connectorId, protocol }: CommandPanelProps) {
  const [form] = Form.useForm<CommandFormValues>();
  const { message, modal } = App.useApp();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<NocSimCommandResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const command = (Form.useWatch('command', form) ?? 'remoteStart') as NocSimCommand;
  const needsConnector = CONNECTOR_COMMANDS.includes(command);

  // Mantiene el conector del formulario en sincronía con el seleccionado arriba.
  useEffect(() => {
    if (connectorId) {
      form.setFieldValue('connectorId', connectorId);
    }
  }, [connectorId, form]);

  const execute = async (values: CommandFormValues) => {
    if (!stationId) {
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const payload: Record<string, unknown> = {};
      if (values.command === 'remoteStart' && values.idTag) {
        payload.idTag = values.idTag;
      } else if (values.command === 'reset') {
        payload.type = values.resetType ?? 'Immediate';
      } else if (values.command === 'triggerMessage') {
        payload.requestedMessage = values.requestedMessage ?? 'MeterValues';
      } else if (values.command === 'setChargingProfile') {
        payload.csChargingProfiles = buildChargingProfile(values.limitA ?? 16);
      }
      const response = await simApi.command({
        stationId,
        connectorId: needsConnector ? values.connectorId ?? connectorId ?? 1 : undefined,
        command: values.command,
        payload,
      });
      setResult(response);
      message.success(`Comando ${values.command} enviado.`);
    } catch (caught) {
      const detail = caught instanceof ApiError ? caught.message : 'No se pudo enviar el comando.';
      setError(detail);
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = (values: CommandFormValues) => {
    // La confirmación se decide con el comando efectivamente enviado (no con el
    // estado observado), para no depender del timing de `Form.useWatch`.
    if (NOC_SIM_DESTRUCTIVE_COMMANDS.includes(values.command)) {
      modal.confirm({
        title: 'Acción destructiva',
        content: `«${COMMAND_LABELS[values.command]}» interrumpe la operación del cargador. ¿Confirmas el envío?`,
        okText: 'Sí, enviar',
        cancelText: 'Cancelar',
        okButtonProps: { danger: true },
        onOk: () => execute(values),
      });
      return;
    }
    void execute(values);
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ command: 'remoteStart', connectorId: connectorId ?? 1, resetType: 'Immediate', requestedMessage: 'MeterValues', limitA: 16 }}
        requiredMark={false}
      >
        <Form.Item label="Comando" name="command" rules={[{ required: true }]}>
          <Select
            options={NOC_SIM_COMMANDS.map((value) => ({
              value,
              label: `${COMMAND_LABELS[value]}${
                NOC_SIM_DESTRUCTIVE_COMMANDS.includes(value) ? '  ⚠ destructivo' : ''
              }`,
            }))}
          />
        </Form.Item>

        {needsConnector && (
          <Form.Item label="Conector" name="connectorId" tooltip="connectorId OCPP">
            <InputNumber min={1} max={16} style={{ width: '100%' }} />
          </Form.Item>
        )}

        {command === 'remoteStart' && (
          <Form.Item label="idTag (opcional)" name="idTag" tooltip="Si se omite, el backend usa el idTag de demo">
            <Input placeholder="ABCD1234" allowClear />
          </Form.Item>
        )}

        {command === 'reset' && (
          <Form.Item label="Tipo de reset" name="resetType">
            <Select options={RESET_TYPES.map((value) => ({ value, label: value }))} />
          </Form.Item>
        )}

        {command === 'triggerMessage' && (
          <Form.Item label="Mensaje solicitado" name="requestedMessage">
            <Select options={TRIGGER_MESSAGES.map((value) => ({ value, label: value }))} />
          </Form.Item>
        )}

        {command === 'setChargingProfile' && (
          <Form.Item label="Límite del perfil (A)" name="limitA" tooltip="TxDefaultProfile recurrente en amperios">
            <InputNumber min={6} max={32} style={{ width: '100%' }} />
          </Form.Item>
        )}

        <Space wrap>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SendOutlined />}
            loading={submitting}
            disabled={!stationId}
          >
            Enviar comando
          </Button>
          <Typography.Text type="secondary">
            {stationId ? `Destino: ${stationId} · ${protocol ?? 'protocolo ?'}` : 'Selecciona una estación'}
          </Typography.Text>
        </Space>
      </Form>

      {error && <Typography.Text type="danger">{error}</Typography.Text>}
      {result && <CommandResultView result={result} />}
    </Space>
  );
}
